import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import CircularProgress from '@mui/material/CircularProgress';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { Link as RouterLink } from 'react-router-dom';

import StaticAdsService from 'services/StaticAdsService';

import { StaticPrompt } from './types';

// The static prompt pipeline, shown as WHAT IT IS: staged and locked.
//
// This used to be an editor. It is deliberately not one any more: the three
// prompts are contracts, not copy — 1a and 1b's output schemas are parsed into
// the analysis tables, and prompt 2's into the build sheet. Rewording a section
// or renaming a schema key breaks that parsing on the next Analyze, after the
// model call was already paid for. The backend refuses writes (423) even if a
// stale build of this page still offers a form; changes ship through a PR where
// the parsers can move in the same commit.

/** Display order and framing per role. */
const STAGES: Array<{
  title: string;
  blurb: string;
  roles: Array<{ role: string; tag: string }>;
  /** How many prompts this stage is supposed to hold, stated so a wrong count is visible. */
  expects: string;
}> = [
  {
    title: 'Stage 1 — Structured analysis',
    blurb: 'Run together when you hit Analyze. Each reads the ad image and returns JSON; '
      + 'their outputs fill the analysis tables and the library facets, and both feed stage 2.',
    roles: [
      { role: 'build_record', tag: '1a · Build record' },
      { role: 'teardown', tag: '1b · Creative teardown' },
    ],
    expects: 'one or more prompts',
  },
  {
    title: 'Stage 2 — Creative generation',
    blurb: 'Consumes the stage 1 outputs plus the original image (and an optional remake brief) '
      + 'and emits the build sheet the image generation runs from.',
    roles: [{ role: 'build_sheet', tag: '2 · Build sheet' }],
    expects: 'exactly one prompt',
  },
];

const KNOWN_ROLES = new Set(STAGES.flatMap(s => s.roles.map(r => r.role)));

function PromptCard({ prompt, tag }: { prompt: StaticPrompt; tag: string }) {
  return (
    <Accordion variant="outlined" disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', pr: 1 }}>
          <Chip size="small" color="primary" label={tag} />
          <Typography variant="subtitle2">{prompt.name}</Typography>
          <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.disabled">
            {prompt.promptText.length.toLocaleString()} chars
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {/* The full text, readable but visibly not a form. */}
        <Box
          component="pre"
          sx={{
            m: 0, p: 1.5, borderRadius: 1, bgcolor: 'grey.50',
            border: '1px solid', borderColor: 'divider',
            fontSize: '0.75rem', lineHeight: 1.5,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            maxHeight: 480, overflowY: 'auto',
          }}
        >
          {prompt.promptText}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default function PromptEditor() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [prompts, setPrompts] = useState<StaticPrompt[] | null>(null);
  // A failed load must not render as an empty registry — every stage would
  // show a false "no prompt holds this role" error.
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setPrompts(null);
    setLoadError(false);
    void service.listPrompts().then((resp) => {
      if (resp) setPrompts(resp.prompts || []);
      else setLoadError(true);
    });
  }, [service]);

  useEffect(() => { load(); }, [load]);

  const byRole = (role: string) => (prompts || []).filter(p => p.role === role);
  const unassigned = (prompts || []).filter(p => !p.role || !KNOWN_ROLES.has(p.role));

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Alert severity="info" icon={<LockOutlinedIcon fontSize="small" />} sx={{ mb: 3 }}>
        These prompts are <strong>locked</strong>. Their output schemas feed the analysis tables
        directly — the facets, the on-image elements, the build sheet — so a wording change can
        break parsing on the next Analyze. Edits ship through an engineer as a PR, alongside the
        matching parser changes.
      </Alert>

      {loadError && (
        <Alert
          severity="error" sx={{ mb: 4 }}
          action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
        >
          Couldn&apos;t load the prompt registry — the API may be down.
        </Alert>
      )}
      {!loadError && !prompts && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {!loadError && prompts && STAGES.map(stage => (
        <Box key={stage.title} sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>{stage.title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {stage.blurb}
          </Typography>

          <Stack spacing={1}>
            {stage.roles.map(({ role, tag }) => {
              const matches = byRole(role);
              if (!matches.length) {
                return (
                  // A missing stage prompt means Analyze fails loudly — say so
                  // here, where someone would come looking for the reason.
                  <Alert key={role} severity="error">
                    No prompt holds the <code>{role}</code> role — the pipeline cannot run this
                    stage until an engineer restores it.
                  </Alert>
                );
              }
              return matches.map(prompt => (
                <PromptCard key={prompt.promptId} prompt={prompt} tag={tag} />
              ));
            })}
          </Stack>

          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
            This stage expects {stage.expects}.
          </Typography>
        </Box>
      ))}

      {unassigned.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>Not part of the pipeline</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {/* Honest rather than hidden: a prompt sitting here does nothing,
                and hiding it would make that impossible to discover. */}
            These prompts carry no stage role, so nothing runs them.
          </Typography>
          <Stack spacing={1}>
            {unassigned.map(prompt => (
              <PromptCard key={prompt.promptId} prompt={prompt} tag="unassigned" />
            ))}
          </Stack>
        </Box>
      )}

      {/* Formats moved out. Two editors over one registry drift, and an
          editable section directly beneath "these prompts are LOCKED" made this
          page contradict itself. */}
      <Alert severity="info" sx={{ mb: 4 }}>
        Layout formats moved to the{' '}
        <Link component={RouterLink} to="/static-ads/formats">Formats tab</Link>, where they can now
        be created and deleted as well as edited.
      </Alert>
    </Box>
  );
}
